import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { TableName } from '../../constants/table';
import {
  getByPartitionKey,
  deleteEntity,
} from '../../lib/services/api-service';

interface CompanyMappingRecord {
  PartitionKey: string;
  RowKey: string;
  CompanyOCR: string;
  CompanyId: string;
  IsSystemGenerated?: boolean;
}

interface CompanyMappingsDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  tenantId: string;
  onMappingsChanged: () => void;
}

const CompanyMappingsDialog: React.FC<CompanyMappingsDialogProps> = ({
  open,
  onClose,
  companyId,
  companyName,
  tenantId,
  onMappingsChanged,
}) => {
  const [mappings, setMappings] = useState<CompanyMappingRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const partitionKey = tenantId.toLowerCase();

      // Fetch all CompanyMapping records for this tenant and filter by companyId
      const allMappings: CompanyMappingRecord[] = await getByPartitionKey(
        TableName.CompanyMapping,
        partitionKey,
      );

      const companyMappings = allMappings.filter(
        (m) =>
          m.CompanyId === companyId &&
          m.CompanyOCR?.toLowerCase() !== companyName?.toLowerCase()
      );

      setMappings(companyMappings);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      toast.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && companyId) {
      fetchMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const handleDeleteMapping = async (mapping: CompanyMappingRecord) => {
    setDeleting(mapping.RowKey);
    try {
      await deleteEntity(
        TableName.CompanyMapping,
        mapping.PartitionKey,
        mapping.RowKey
      );

      toast.success(`Deleted mapping: ${mapping.CompanyOCR}`);
      fetchMappings();
      onMappingsChanged();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error('Failed to delete mapping');
    } finally {
      setDeleting(null);
    }
  };

  const totalMappingsCount = 1 + mappings.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
            <div>
                Mappings for <strong>{companyName}</strong>
            </div>
            <div style={{ fontSize: '0.875rem', color: 'gray' }}>
                {totalMappingsCount} mapping(s) found
            </div>
        </div>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Company OCR / Name Variation</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Primary Mapping (Company Itself) */}
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>
                    {companyName}
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: '0.75rem',
                        color: 'gray',
                      }}>
                      (Primary Match)
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Primary mapping cannot be deleted">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled
                          onClick={() => {}}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Other Mappings from CompanyMapping table */}
                {mappings.map(mapping => (
                  <TableRow key={mapping.RowKey}>
                    <TableCell>{mapping.CompanyOCR}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete mapping">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleting === mapping.RowKey}
                            onClick={() => handleDeleteMapping(mapping)}>
                            {deleting === mapping.RowKey ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyMappingsDialog;
